/**
 * Цели Яндекс.Метрики (#212).
 *
 * Номер счётчика берётся из `window.__ymId`, который ставит сам
 * загрузчик (`components/seo/metrika.tsx`). Это разом решает три вопроса:
 *
 * - на стенде и на localhost счётчик не запускается, `__ymId` там нет, и
 *   цели молчат, не засоряя отчёты прода проверками;
 * - на других продуктах (DataHunter, INGDG) Метрики нет, и общий код
 *   кабинета не отправит туда ничего;
 * - вызывающему не нужно знать номер и тянуть реестр продуктов в браузер.
 *
 * Параметры целей только служебные: страница, тариф, сумма. Ни текста
 * запросов, ни почты, ни имени: это не наше дело отправлять в чужую
 * аналитику.
 */

declare global {
  interface Window {
    __ymId?: string;
    ym?: (id: number | string, method: string, ...args: unknown[]) => void;
  }
}

/** Имена целей. Латиницей: так их проще завести в интерфейсе Метрики. */
export type Cel =
  | 'sign_up'
  | 'login'
  | 'first_request'
  | 'request'
  // Попытка запроса к нейросети и отказ (Seo-marketing, 30.10.2026): на
  // /text-to-speech и /gpt/rewrite сотни входов и почти ноль «request»,
  // и без попыток не понять — не нажимают или нажимают и получают отказ.
  // Параметры — раздел сайта и код отказа, без текста запроса.
  | 'request_popytka'
  | 'request_otkaz'
  // 3D-модель по описанию (/aimedia/3d-model): заказ принят и модель
  // готова. Сорвавшаяся — request_otkaz с kod=cad_fail (Seo-marketing).
  | 'cad_zakaz'
  | 'cad_gotovo'
  | 'pricing_open'
  | 'payment_start'
  | 'payment_success'
  | 'tariff_connect'
  // Нажатие любой кнопки чата поддержки (#218), параметр — страница.
  | 'chat_open'
  // То же, но из раздела для бизнеса: заявка раздела отдельной целью,
  // чтобы видеть её в отчёте конверсий без фильтра по параметрам.
  | 'business_chat'
  // Покупка лицензии на программу без входа (#242): открыл форму,
  // перешёл к оплате, оплатил. Параметр — товар; почта и имя не уходят.
  | 'license_form_open'
  | 'license_checkout'
  | 'license_paid'
  // Нажатие «Прежняя версия» в плашке aisearch.ru, параметр — страница.
  | 'legacy_back'
  // Режим «Про» чата (AI-Tech-API#238): человек выбрал «Про»; упёрся в
  // деньги (402); пополнил кошелёк, придя из этой развилки. Параметры —
  // раздел сайта и задача, без текста запроса и без суммы баланса.
  | 'pro_vybran'
  | 'pro_402'
  | 'pro_popolnil'
  // Выбор покупки пережил вход: показан шаг «Продолжить покупку» или
  // плашка кабинета, и покупка с этого шага завершена. Параметры — вид
  // покупки (tarif, paket_katalog…), откуда пришли и состояние шага;
  // ни кода предложения, ни пользователя, ни почты.
  | 'pokupka_prodolzhit_pokazano'
  | 'pokupka_posle_vhoda_zavershena'
  // «Удвоение для старичков» (AI-Tech-API#276): плашка «С возвращением!»
  // показана на главной кабинета; пополнение удвоено (бонус заведён —
  // виден на балансе после оплаты). Без параметров: ни суммы, ни почты.
  | 'x2_pokaz'
  | 'x2_popolnil'
  // Колокольчик кабинета aisearch.ru: открыл список; отложил плашку
  // «Позже» (параметр — какую); дал согласие на новости из колокольчика.
  | 'nfy_open'
  | 'nfy_pozzhe'
  | 'nfy_soglasie';

/** Цели нажатия кнопки чата: общая и, для раздела бизнеса, своя. */
export function celChata(): void {
  if (!ymId()) return;
  const stranica = razdel(window.location.pathname);
  cel('chat_open', { stranica });
  if (stranica === '/business' || stranica.startsWith('/business/')) {
    cel('business_chat', { stranica });
  }
}

export function ymId(): string | undefined {
  if (typeof window === 'undefined') return undefined;
  return window.__ymId;
}

export function cel(imya: Cel, params?: Record<string, string | number>): void {
  const id = ymId();
  if (!id || !window.ym) return;
  try {
    window.ym(id, 'reachGoal', imya, params);
  } catch {
    // Аналитика не вправе ломать то, ради чего человек пришёл.
  }
}

/**
 * Цель перед уходом со страницы: ждёт отправки, но не дольше `ms`.
 *
 * Вход и регистрация заканчиваются полной перезагрузкой (signIn уводит
 * браузер), и отправленная обычным вызовом цель может не успеть уйти.
 * Метрика умеет сообщить об отправке колбэком, его и ждём. Предел
 * времени обязателен: заблокированный счётчик не должен держать
 * человека на форме входа.
 */
export function celIZhdat(
  imya: Cel,
  params?: Record<string, string | number>,
  ms = 300,
): Promise<void> {
  const id = ymId();
  if (!id || !window.ym) return Promise.resolve();
  return new Promise((resolve) => {
    const t = window.setTimeout(resolve, ms);
    try {
      window.ym?.(id, 'reachGoal', imya, params, () => {
        window.clearTimeout(t);
        resolve();
      });
    } catch {
      window.clearTimeout(t);
      resolve();
    }
  });
}

/**
 * Адрес без изменчивых частей, чтобы цели складывались по разделам.
 *
 * `/dashboard/chat/5f1c…` превращается в `/dashboard/chat/:id`: иначе
 * у каждого разговора была бы своя строка отчёта, и по инструментам
 * ничего не сложить.
 */
export function razdel(pathname: string): string {
  return pathname
    .split('/')
    .map((s) => (/^[0-9a-f-]{16,}$/i.test(s) || /^\d+$/.test(s) ? ':id' : s))
    .join('/');
}

const PERVYJ = 'ais-ym-first-request';

/**
 * Запрос к нейросети: общая цель на каждый и отдельная на самый первый.
 *
 * «Первый» считается по браузеру (localStorage), а не по аккаунту: сервер
 * этого не сообщает, а для воронки «пришёл из поиска → попробовал» этого
 * и нужно. В приватном режиме запись запрещена, и тогда первым будет
 * каждый запрос вкладки: занижать воронку хуже, чем слегка завысить.
 */
export function celZapros(): void {
  if (!ymId()) return;
  const stranica = razdel(window.location.pathname);
  cel('request', { stranica });
  let uzhe = false;
  try {
    uzhe = window.localStorage.getItem(PERVYJ) === '1';
    if (!uzhe) window.localStorage.setItem(PERVYJ, '1');
  } catch {
    // Приватный режим: считаем первым.
  }
  if (!uzhe) cel('first_request', { stranica });
}

/** Человек отправил запрос к нейросети: до ответа и до проверки лимитов. */
export function celPopytki(): void {
  if (!ymId()) return;
  cel('request_popytka', { stranica: razdel(window.location.pathname) });
}

/** Запрос не дошёл до ответа: код отказа (лимит, сеть, http_502…). */
export function celOtkaza(kod: string): void {
  if (!ymId()) return;
  cel('request_otkaz', { stranica: razdel(window.location.pathname), kod });
}
